import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { fetchBonusCampaignsAction, postClaimBonusAction } from '../../store/apiActions/fetchBonusPromotionAction';
import { getTotalBalanceFromState } from '../../utils/chips';
import {
  getFreeChipOnZeroBalance,
  getNewUserDefaultChip,
  getNewUserFreeChip,
  getNewUserNoDefaultChip,
  getUserCasinoChip,
} from '../../utils/chipSelection';
import { fetchUserInfoAction } from '../apiActions/fetchUserInfoAction';
import type { CoreRootState } from '../CoreStore';
import { chipActions } from '../slices/chips/slice';
import type { ChipValueType } from '../slices/chips/types';
import { ChipSelectionOrigin, ChipTypes } from '../slices/chips/types';
import { GameStatus } from '../slices/game/types';

// This middleware listens for fetchUserInfoAction.fulfilled and selects chip for user on initial game load.
// Depends on fetchUserInfoAction promise, because selectMaxAffordableChip() requires balance data.

export const chipSelectionMiddleware = createListenerMiddleware<CoreRootState>();

chipSelectionMiddleware.startListening({
  predicate: (action, currentState, originalState) => {
    const isMatchingAction = isAnyOf(fetchUserInfoAction.fulfilled, fetchBonusCampaignsAction.fulfilled)(action);

    const isBetsOpen =
      currentState.gameState.currentStatus === GameStatus.PlaceYourBets &&
      currentState.gameState.currentStatus !== originalState.gameState.currentStatus;

    return isMatchingAction || isBetsOpen;
  },
  effect: (_, { dispatch, getState }) => {
    const state = getState();
    const selectedChipFromConfig = state.config.playerConfig?.selectedChipIndex;
    const freeChipSelectedInConfig = state.config.playerConfig?.freeChipsParamsRaw?.selected_chip_fb;
    const activeBonusCampaign = state.bonusPromotions.activeBonusCampaign;
    const defaultChipWeight = state.config.tableConfig?.defaultChipOrder;
    const minBetLimit = state.config.tableConfig?.tableBetLimit?.min ?? Number.MIN_VALUE;
    const balance = getTotalBalanceFromState(state);
    // max affordable chip logic is used only in casinoChips.
    const chips = state.chips.chipsData.casinoChips;
    const isFreeChipSelected = state.chips.selectedChip?.type === ChipTypes.FreeChip;
    const userSelectedChipWeight = state.chips.selectedChip?.weight;
    const resetSelectedChip = isFreeChipSelected && !activeBonusCampaign;
    const userInfo = state.gameState.userInfo;
    const totalBet = state.bets.totalBet;
    const placedBetType = state.bets.placedBetType;
    let selectedChip: ChipValueType | null = null;

    if (!userSelectedChipWeight || resetSelectedChip) {
      if (!resetSelectedChip && freeChipSelectedInConfig) {
        if (activeBonusCampaign) {
          // Scenario 1: User joins table. Select free chip if it is available and selected in config.
          selectedChip = getNewUserFreeChip(activeBonusCampaign);
        }
      } else if (selectedChipFromConfig === undefined) {
        // Scenario 2: New user joins table. If default chip is set in table config, Select closes chip to default
        // else select lowest chip
        if (defaultChipWeight) {
          selectedChip = getNewUserDefaultChip(defaultChipWeight, chips, userInfo, totalBet);
        } else {
          selectedChip = getNewUserNoDefaultChip(chips, userInfo, totalBet);
        }
      } else {
        // Scenario 3: Returning user joins table.
        // Select chip from player config if it is a casino chip.
        selectedChip = getUserCasinoChip(selectedChipFromConfig, chips);
      }
    }

    //Scenario 4: If casinoBalance is less than min bet limit
    //select FreeChip by default if available
    if (balance < minBetLimit && activeBonusCampaign && !placedBetType && !isFreeChipSelected) {
      selectedChip = getFreeChipOnZeroBalance(activeBonusCampaign);
    }

    if (selectedChip) {
      dispatch(chipActions.setSelectedChip(selectedChip));
    }
  },
});

chipSelectionMiddleware.startListening({
  matcher: isAnyOf(postClaimBonusAction.fulfilled),
  effect: (_, { dispatch, getState }) => {
    const state = getState();
    const activeBonusCampaign = state.bonusPromotions.activeBonusCampaign;
    const isFreeChipSelected = state.chips.selectedChip?.type === ChipTypes.FreeChip;

    // Scenario 1: After claiming the bonus set selected chip to free chip.
    if (activeBonusCampaign !== null && !isFreeChipSelected) {
      const selectedChip = getNewUserFreeChip(activeBonusCampaign, ChipSelectionOrigin.System);
      if (selectedChip) {
        dispatch(chipActions.setAndSyncSelectedChip(selectedChip));
      }
    }
  },
});
